import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/env';
import * as jwt_decode from 'jwt-decode';
import { catchError, lastValueFrom, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggedIn = true;
  private CHECKOUT_API_URL = environment.CHECKOUT_API_URL;

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {
  }

  async login(email: string, password: string, newPassword: string | null, otp: string | null): Promise<LoginResponse> {
    const observable = this.http
      .post<LoginResponse>(`${this.CHECKOUT_API_URL}/auth`, {
        email,
        password,
        newPassword,
        otp
      })
      .pipe(
        map((response) => {
          this.setCookie('accessToken', response.access_token, 1);
          this.setCookie('refreshToken', response.refresh_token, 1);
          this.setCookie('isLoggedIn', 'true', 1);
          return response;
        }),
        catchError(this.handleError),
      );

    return await lastValueFrom(observable);
  }

  getAccessToken(): HttpHeaders {
    const token = this.getCookie('accessToken');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  logout(): void {
    this.deleteCookie('accessToken');
    this.deleteCookie('isLoggedIn');
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.getCookie('isLoggedIn') === 'true';
  }

  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict; Secure`;
  }

  getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure`;
  }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred:', error);
    throw error;
  }

  getUsernameFromToken(): string | null {
    const token = this.getCookie('accessToken');
    if (token) {
      const decodedToken = jwt_decode.jwtDecode(token) as { name: string };
      if (decodedToken.name) {
        const uniqueNames = [...new Set(decodedToken.name.split(' '))];
        return uniqueNames.slice(0, 2).join(' ');
      }
    }
    return null;
  }

  getRolesFromToken(): string[] | null {
    const token = this.getCookie('accessToken');
    if (token) {
      const decodedToken: any = jwt_decode.jwtDecode(token);
      return decodedToken.realm_access?.roles || [];
    }
    return null;
  }

  getLoggedUserId(): string {
    const token = this.getCookie('accessToken');
    if (token) {
      const decodedToken: any = jwt_decode.jwtDecode(token);
      return decodedToken.sub;
    }
    return '';
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const observable = this.http
      .post<LoginResponse>(`${this.CHECKOUT_API_URL}/auth/refresh-token`, {
        refreshToken,
      })
      .pipe(
        map((response) => {
          this.isLoggedIn = true;
          this.setCookie('accessToken', response.access_token, 1);
          this.setCookie('refreshToken', response.refresh_token, 1);
          this.setCookie('isLoggedIn', 'true', 1);
          return response;
        }),
        catchError(this.handleError),
      );

    return await lastValueFrom(observable);
  }

  async sendVerifyCode(email: string, code: string): Promise<LoginResponse> {
    const observable = this.http
      .post<LoginResponse>(`${this.CHECKOUT_API_URL}/auth/verify-code`, {
        email,
        code,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(this.handleError),
      );

    return await lastValueFrom(observable);
  }

  async sendCode(email: string): Promise<LoginResponse> {
    const observable = this.http
      .post<LoginResponse>(`${this.CHECKOUT_API_URL}/auth/send-code`, {
        email, origin: 'admin'
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(this.handleError),
      );

    return await lastValueFrom(observable);
  }
}

export interface LoginResponse {
  access_token: string;
  expires_in: string;
  refresh_expires_in: string;
  refresh_token: string;
}
